import type { DspModuleDescriptor, DspPortDescriptor } from '../../dsp-module-contract/src/index.js';
import { validatePatchGraph, type PatchConnectionV1, type PatchEndpoint, type PatchGraphV1, type PatchNodeV1, type PatchPosition } from '../../routing-graph/src/index.js';

export type EditResult = Readonly<{ ok: true } | { ok: false; error: string }>;
export interface PatchViewPort extends DspPortDescriptor {}
export interface PatchViewNode {
  readonly id: string;
  readonly moduleId: string;
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly inputs: readonly PatchViewPort[];
  readonly outputs: readonly PatchViewPort[];
}
export interface PatchViewCable {
  readonly id: string;
  readonly kind: DspPortDescriptor['kind'];
  readonly source: PatchEndpoint;
  readonly target: PatchEndpoint;
}
export interface PatchView {
  readonly nodes: readonly PatchViewNode[];
  readonly cables: readonly PatchViewCable[];
}
const success: EditResult = Object.freeze({ ok: true });

export class PatchEditor {
  private current: PatchGraphV1;
  private readonly descriptors: readonly DspModuleDescriptor[];

  constructor(graph: PatchGraphV1, descriptors: readonly DspModuleDescriptor[]) {
    validatePatchGraph(graph, descriptors);
    this.current = graph;
    this.descriptors = descriptors;
  }
  get graph(): PatchGraphV1 { return this.current; }

  private commit(candidate: PatchGraphV1): EditResult {
    try {
      validatePatchGraph(candidate, this.descriptors);
      this.current = candidate;
      return success;
    } catch (error) {
      return Object.freeze({ ok: false, error: error instanceof Error ? error.message : String(error) });
    }
  }

  addNode(node: PatchNodeV1): EditResult {
    return this.commit({ ...this.current, nodes: [...this.current.nodes, node] });
  }

  /** Atomically replace the patch. A failed import never changes the current graph. */
  replaceGraph(graph: PatchGraphV1): EditResult {
    return this.commit(graph);
  }

  removeNode(nodeId: string): EditResult {
    if (!this.current.nodes.some((node) => node.id === nodeId)) {
      return Object.freeze({ ok: false, error: `node does not exist: ${nodeId}` });
    }
    return this.commit({
      ...this.current,
      nodes: this.current.nodes.filter((node) => node.id !== nodeId),
      connections: this.current.connections.filter((connection) =>
        connection.source.nodeId !== nodeId && connection.target.nodeId !== nodeId),
    });
  }

  connect(connection: PatchConnectionV1): EditResult {
    return this.commit({ ...this.current, connections: [...this.current.connections, connection] });
  }

  disconnect(connectionId: string): EditResult {
    if (!this.current.connections.some((connection) => connection.id === connectionId)) {
      return Object.freeze({ ok: false, error: `connection does not exist: ${connectionId}` });
    }
    return this.commit({ ...this.current, connections: this.current.connections.filter((connection) => connection.id !== connectionId) });
  }

  moveNode(nodeId: string, position: PatchPosition): EditResult {
    if (!this.current.nodes.some((node) => node.id === nodeId)) return Object.freeze({ ok: false, error: `node does not exist: ${nodeId}` });
    return this.commit({ ...this.current, nodes: this.current.nodes.map((node) => node.id === nodeId ? { ...node, position } : node) });
  }

  /** Deterministic, renderer-neutral visual readback for DOM, Canvas or native hosts. */
  getView(): PatchView {
    const descriptors = new Map(this.descriptors.map((descriptor) => [descriptor.id, descriptor]));
    const nodes = this.current.nodes.map((node, index): PatchViewNode => {
      const descriptor = descriptors.get(node.moduleId)!;
      return Object.freeze({
        id: node.id,
        moduleId: node.moduleId,
        label: descriptor.name,
        x: node.position?.x ?? index * 180,
        y: node.position?.y ?? 0,
        inputs: descriptor.inputs,
        outputs: descriptor.outputs,
      });
    });
    const graphNodes = new Map(this.current.nodes.map((node) => [node.id, node]));
    const cables = this.current.connections.map((connection): PatchViewCable => {
      const moduleId = graphNodes.get(connection.source.nodeId)!.moduleId;
      const sourceDescriptor = descriptors.get(moduleId)!;
      const kind = sourceDescriptor.outputs.find((port) => port.id === connection.source.portId)!.kind;
      return Object.freeze({
        id: connection.id,
        kind,
        source: connection.source,
        target: connection.target,
      });
    });
    return Object.freeze({ nodes: Object.freeze(nodes), cables: Object.freeze(cables) });
  }
}
